import { awaitMediaOperation, waitForMediaPosition } from './quality-preparation.ts';

export type NativeOutputIntent = {
  position: number; autoplay: boolean; playbackRate: number; volume: number; muted: boolean; revision: number;
};
type OutputHooks = {
  bind: (media: HTMLAudioElement, current: () => boolean) => () => void;
  change: (previous: HTMLAudioElement, current: HTMLAudioElement) => void;
};
type PromotionOptions = {
  signal: AbortSignal;
  current: () => boolean;
  intent: () => NativeOutputIntent;
  activated?: () => void;
  restored?: () => void;
  timeoutMs?: number;
};
type Transfer = {
  previous: HTMLAudioElement; candidate: HTMLAudioElement; controller: AbortController;
  options: PromotionOptions; committed: boolean; latest: NativeOutputIntent; previousUrl: string | null;
};
const cancelError = () => new DOMException('音质切换已取消', 'AbortError');

export function releaseNativeMedia(media: HTMLAudioElement) {
  media.muted = true; media.volume = 0; media.pause(); media.removeAttribute('src'); media.load();
}

/** One audible owner. A pending decoder never owns UI/events/listening time. */
export class NativeAudioOutput {
  private media: HTMLAudioElement;
  private hooks: OutputHooks;
  private disposeEvents: () => void;
  private generation = 0;
  private transfer: Transfer | null = null;

  constructor(initial: HTMLAudioElement, hooks: OutputHooks) {
    this.media = initial; this.hooks = hooks;
    this.disposeEvents = this.bind(initial);
  }
  get active() { return this.media; }

  /** Replace a stopped source while keeping the event owner singular. */
  replaceStopped(next: HTMLAudioElement) {
    this.cancel();
    const previous = this.media;
    previous.pause();
    this.activate(next);
    releaseNativeMedia(previous);
  }

  private bind(media: HTMLAudioElement) {
    const generation = this.generation;
    return this.hooks.bind(media, () => this.media === media && this.generation === generation);
  }
  private activate(next: HTMLAudioElement) {
    const previous = this.media;
    this.disposeEvents(); this.generation += 1; this.media = next;
    this.hooks.change(previous, next);
    this.disposeEvents = this.bind(next);
  }
  private assertCurrent(transfer: Transfer) {
    if (this.transfer !== transfer || transfer.controller.signal.aborted || !transfer.options.current()) throw cancelError();
  }
  private readIntent(transfer: Transfer) {
    transfer.latest = transfer.options.intent();
    return transfer.latest;
  }
  private restore(transfer: Transfer) {
    const { previous, candidate } = transfer;
    if (!transfer.committed || this.media !== candidate) { releaseNativeMedia(candidate); return; }
    // Read the latest controls from the still-active candidate before changing
    // the owner. A failed request must not restore its click-time pause/seek.
    const latest = this.readIntent(transfer);
    candidate.muted = true; candidate.pause();
    this.activate(previous);
    previous.playbackRate = latest.playbackRate; previous.defaultPlaybackRate = latest.playbackRate;
    previous.volume = latest.volume; previous.muted = latest.muted;
    if (Math.abs(previous.currentTime - latest.position) > .04) previous.currentTime = latest.position;
    transfer.options.restored?.();
    if (latest.autoplay) {
      // Keep the old decoder/buffer rather than loading its URL again. If a
      // newer request moves this old element elsewhere, late play settlement
      // may only stop that retired original resource, never the new track.
      void previous.play().then(() => {
        if (this.media !== previous && previous.getAttribute('src') === transfer.previousUrl) previous.pause();
      }).catch(() => {});
    } else previous.pause();
    releaseNativeMedia(candidate);
  }
  cancel() {
    this.transfer?.controller.abort(cancelError());
  }

  async promote(candidate: HTMLAudioElement, options: PromotionOptions): Promise<void> {
    if (options.signal.aborted) { releaseNativeMedia(candidate); throw cancelError(); }
    this.cancel();
    const transfer: Transfer = {
      previous: this.media, previousUrl: this.media.getAttribute('src'), candidate,
      controller: new AbortController(), options, committed: false, latest: options.intent(),
    };
    this.transfer = transfer;
    const signal = transfer.controller.signal;
    const externalAbort = () => transfer.controller.abort(options.signal.reason || cancelError());
    const cancelled = () => {
      if (this.transfer !== transfer) return;
      this.transfer = null;
      this.restore(transfer);
    };
    signal.addEventListener('abort', cancelled, { once: true });
    options.signal.addEventListener('abort', externalAbort, { once: true });
    const deadline = setTimeout(() => transfer.controller.abort(new Error('音质候选晋升超时')), options.timeoutMs ?? 15000);
    try {
      candidate.muted = true; candidate.volume = 0;
      // There is no public playback request during warming: the old element
      // continues owning all events, MediaSession and lyric/listening clocks.
      for (;;) {
        this.assertCurrent(transfer);
        const before = this.readIntent(transfer);
        candidate.playbackRate = before.playbackRate; candidate.defaultPlaybackRate = before.playbackRate;
        if (!before.autoplay) candidate.pause();
        await waitForMediaPosition(candidate, () => this.readIntent(transfer).position, signal, options.timeoutMs ?? 15000, false, () => this.readIntent(transfer).revision);
        this.assertCurrent(transfer);
        const ready = this.readIntent(transfer);
        if (ready.autoplay) {
          if (candidate.paused) await awaitMediaOperation(candidate.play(), signal, options.timeoutMs ?? 15000);
          await this.waitForAdvance(candidate, transfer);
        } else candidate.pause();
        this.assertCurrent(transfer);
        const latest = this.readIntent(transfer);
        if (latest.autoplay !== ready.autoplay || latest.revision !== ready.revision || latest.playbackRate !== ready.playbackRate) continue;
        // The two running native clocks now advance at the same rate. A large
        // startup offset is re-prepared while the old output stays audible.
        if (Math.abs(candidate.currentTime - latest.position) > .035) {
          candidate.currentTime = latest.position;
          continue;
        }
        break;
      }
      this.assertCurrent(transfer);
      const latest = this.readIntent(transfer);
      // No await inside this commit. Retire old listeners before its pause,
      // so even same-track pause/timeupdate cannot overwrite the new owner.
      transfer.previous.muted = true;
      this.activate(candidate); transfer.committed = true;
      transfer.previous.pause();
      candidate.volume = latest.volume; candidate.muted = latest.muted;
      options.activated?.();
      if (latest.autoplay) await awaitMediaOperation(candidate.play(), signal, options.timeoutMs ?? 15000);
      else candidate.pause();
      this.assertCurrent(transfer);
      const confirmed = this.readIntent(transfer);
      if (!confirmed.autoplay) candidate.pause();
      else if (candidate.paused || candidate.error) throw new Error('目标音质未能保持播放，已保留原音频');
      releaseNativeMedia(transfer.previous);
      this.transfer = null;
    } catch (error) {
      if (this.transfer === transfer) { this.transfer = null; this.restore(transfer); }
      throw error;
    } finally {
      clearTimeout(deadline);
      options.signal.removeEventListener('abort', externalAbort); signal.removeEventListener('abort', cancelled);
    }
  }

  private waitForAdvance(media: HTMLAudioElement, transfer: Transfer): Promise<void> {
    const signal = transfer.controller.signal, initial = media.currentTime;
    return new Promise((resolve, reject) => {
      let ended = false;
      const finish = (error?: Error) => {
        if (ended) return; ended = true;
        clearInterval(poll); clearTimeout(timeout);
        media.removeEventListener('timeupdate', check); media.removeEventListener('error', failed); signal.removeEventListener('abort', aborted);
        error ? reject(error) : resolve();
      };
      const check = () => {
        if (!this.readIntent(transfer).autoplay) return finish();
        if (media.error) return failed();
        if (!media.paused && media.readyState >= 2 && !media.seeking && (media.currentTime > initial + .001 || media.ended)) finish();
      };
      const failed = () => finish(new Error('目标音质静音预运行失败'));
      const aborted = () => finish(cancelError());
      const poll = setInterval(check, 20);
      const timeout = setTimeout(() => finish(new Error('目标音质未开始推进，已保留原音频')), transfer.options.timeoutMs ?? 15000);
      media.addEventListener('timeupdate', check); media.addEventListener('error', failed); signal.addEventListener('abort', aborted, { once: true });
      check();
    });
  }
}
